import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { submitContact } from "@/lib/api";

const INQUIRY_TYPES = [
  { value: "general", label: "General Inquiry" },
  { value: "agency", label: "Agency Partnership" },
  { value: "volunteer", label: "Volunteer" },
  { value: "partner", label: "Business / Community Partner" },
  { value: "events", label: "Events & Sponsorship" },
  { value: "family_support", label: "Family Support Request" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  inquiry_type: "general",
  subject: "",
  message: "",
};

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact({
        ...form,
        subject: form.subject || `${INQUIRY_TYPES.find((t) => t.value === form.inquiry_type)?.label || "Inquiry"} from website`,
      });
      toast.success("Thank you. We'll respond within 24–48 hours.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
    >
      <div className="md:col-span-1">
        <label className="eyebrow text-slate-500">Full Name</label>
        <input
          data-testid="contact-name"
          value={form.name}
          onChange={change("name")}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-lg font-medium transition-colors"
          placeholder="Jane Doe"
          required
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow text-slate-500">Email</label>
        <input
          data-testid="contact-email"
          type="email"
          value={form.email}
          onChange={change("email")}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-lg font-medium transition-colors"
          placeholder="you@example.com"
          required
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow text-slate-500">Phone (optional)</label>
        <input
          data-testid="contact-phone"
          value={form.phone}
          onChange={change("phone")}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-lg font-medium transition-colors"
          placeholder="(614) 555-0100"
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow text-slate-500">Organization / Agency</label>
        <input
          data-testid="contact-organization"
          value={form.organization}
          onChange={change("organization")}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-lg font-medium transition-colors"
          placeholder="Columbus PD, First Baptist, …"
        />
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow text-slate-500">Reason</label>
        <select
          data-testid="contact-type"
          value={form.inquiry_type}
          onChange={change("inquiry_type")}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-lg font-medium transition-colors"
        >
          {INQUIRY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-1">
        <label className="eyebrow text-slate-500">Subject</label>
        <input
          data-testid="contact-subject"
          value={form.subject}
          onChange={change("subject")}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-lg font-medium transition-colors"
          placeholder="How can we help?"
        />
      </div>
      <div className="md:col-span-2">
        <label className="eyebrow text-slate-500">Message</label>
        <textarea
          data-testid="contact-message"
          value={form.message}
          onChange={change("message")}
          rows={5}
          className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-black outline-none py-3 text-base font-medium resize-none transition-colors"
          placeholder="Tell us about the officer, family, or need…"
          required
        />
      </div>
      <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
        <p className="text-xs text-slate-500 max-w-md">
          Submissions are confidential. We respond within 24–48 hours.
        </p>
        <button
          data-testid="contact-submit"
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-3 bg-black hover:bg-neutral-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send Message
        </button>
      </div>
    </form>
  );
}
